import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Badge,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure
} from '@chakra-ui/react';
import { 
  ChevronRightIcon, 
  AddIcon, 
  EditIcon, 
  DeleteIcon,
  DragHandleIcon,
  ExternalLinkIcon
} from '@chakra-ui/icons';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import apiClient from '../../../utils/apiClient';

const SitesNavigation = () => {
  const [navigations, setNavigations] = useState([]);
  const [selectedNavigation, setSelectedNavigation] = useState(null);
  const [navigationItems, setNavigationItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditItemModalOpen, setIsEditItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [navigationToDelete, setNavigationToDelete] = useState(null);

  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen: isDeleteAlertOpen, onOpen: onDeleteAlertOpen, onClose: onDeleteAlertClose } = useDisclosure();
  const cancelRef = React.useRef();

  const user = JSON.parse(sessionStorage.getItem('user'));
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchNavigations();
    fetchSites();
    fetchPages();
  }, []);

  const fetchNavigations = async () => {
    try {
      const response = await apiClient(`/navigations/${tenantId}`);
      setNavigations(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedNavigation(response.data[0]);
        fetchNavigationItems(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching navigations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNavigationItems = async (navigationId) => {
    try {
      const response = await apiClient(`/navigations/${tenantId}/${navigationId}`);
      console.log('Navigation Items:', response);
      setNavigationItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching navigation items:', error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await apiClient(`/sites/${tenantId}`);
      setSites(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchPages = async () => {
    console.log('Fetching Pages ====');
    try {
      const response = await apiClient(`/pages/${tenantId}`);
      // Handle the nested response structure
      let pagesData = [];
      if (response.success) {
        // Convert numbered object keys to array
        pagesData = Object.keys(response)
          .filter(key => key !== 'success')
          .map(key => response[key]);
      } else if (Array.isArray(response)) {
        pagesData = response;
      } else if (response.data) {
        pagesData = Array.isArray(response.data) ? response.data : [];
      }
      setPages(pagesData);
    } catch (error) {
      console.error('Error fetching pages:', error);
    }
  };

  const createNavigation = async (data) => {
    try {
      await apiClient(`/navigations/${tenantId}`, {
        method: 'POST',
        body: JSON.stringify(data)
      });

      toast({
        title: 'Navigation created',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchNavigations();
      setIsCreateModalOpen(false);
    } catch (error) {
      toast({
        title: 'Error creating navigation',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const deleteNavigation = async () => {
    if (!navigationToDelete) return;

    try {
      await apiClient(`/navigations/${tenantId}/${navigationToDelete.id}`, {
        method: 'DELETE'
      });

      toast({
        title: 'Navigation deleted',
        description: `"${navigationToDelete.name}" has been deleted successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Clear selected navigation if it was the one deleted
      if (selectedNavigation?.id === navigationToDelete.id) {
        setSelectedNavigation(null);
        setNavigationItems([]);
      }

      // Refresh the navigation list
      fetchNavigations();
      
      // Close the alert dialog
      onDeleteAlertClose();
      setNavigationToDelete(null);

    } catch (error) {
      toast({
        title: 'Error deleting navigation',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      onDeleteAlertClose();
    }
  };

  const handleDeleteNavigation = (navigation, event) => {
    // Prevent the navigation selection when clicking delete
    event.stopPropagation();
    setNavigationToDelete(navigation);
    onDeleteAlertOpen();
  };

  const handleNavigationSelect = (navigation) => {
    setSelectedNavigation(navigation);
    fetchNavigationItems(navigation.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setNavigationItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
      // Here you would also send the new order to your API
    }
  };

  const addNavigationItem = () => {
    setEditingItem(null);
    setIsEditItemModalOpen(true);
  };

  const editNavigationItem = (item) => {
    setEditingItem(item);
    setIsEditItemModalOpen(true);
  };

  const deleteNavigationItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this navigation item?')) {
      return;
    }

    try {
      await apiClient(`/navigations/${tenantId}/items/${itemId}`, {
        method: 'DELETE'
      });

      toast({
        title: 'Navigation item deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchNavigationItems(selectedNavigation.id);
    } catch (error) {
      toast({
        title: 'Error deleting navigation item',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Container maxW="container.2xl" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Navigation</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between">
          <Box>
            <Heading size="lg" mb={2}>Navigation Management</Heading>
            <Text color="gray.500">
              Create and manage navigation menus for your websites
            </Text>
          </Box>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={() => setIsCreateModalOpen(true)}
          >
            Create Navigation
          </Button>
        </HStack>

        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
          {/* Navigation List */}
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">Navigation Menus</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={3} align="stretch">
                {navigations.map((nav) => (
                  <Box
                    key={nav.id}
                    p={3}
                    border="1px"
                    borderColor={selectedNavigation?.id === nav.id ? 'blue.300' : 'gray.200'}
                    borderRadius="md"
                    cursor="pointer"
                    _hover={{ borderColor: 'blue.300' }}
                    onClick={() => handleNavigationSelect(nav)}
                    position="relative"
                  >
                    <VStack align="start" spacing={1}>
                      <HStack justify="space-between" width="100%">
                        <VStack align="start" spacing={1} flex={1}>
                          <Text fontWeight="medium">{nav.name}</Text>
                          <Text fontSize="sm" color="gray.500">{nav.handle}</Text>
                          <Badge colorScheme="blue" size="sm">
                            {nav.site_name}
                          </Badge>
                        </VStack>
                        <IconButton
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          aria-label={`Delete ${nav.name}`}
                          onClick={(e) => handleDeleteNavigation(nav, e)}
                          _hover={{ bg: 'red.50' }}
                        />
                      </HStack>
                    </VStack>
                  </Box>
                ))}
                
                {navigations.length === 0 && !loading && (
                  <Text color="gray.500" textAlign="center" py={4}>
                    No navigation menus found. Create your first navigation menu to get started.
                  </Text>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Navigation Structure */}
          <Box gridColumn={{ base: 'span 1', lg: 'span 2' }}>
            <Card bg={cardBg} borderColor={borderColor} variant="outline">
              <CardHeader>
                <HStack justify="space-between">
                  <Heading size="md">
                    {selectedNavigation ? selectedNavigation.name : 'Select Navigation'}
                  </Heading>
                  {selectedNavigation && (
                    <Button
                      size="sm"
                      leftIcon={<AddIcon />}
                      onClick={addNavigationItem}
                    >
                      Add Item
                    </Button>
                  )}
                </HStack>
              </CardHeader>
              <CardBody>
                {selectedNavigation ? (
                  <NavigationTree
                    items={navigationItems}
                    onEdit={editNavigationItem}
                    onDelete={deleteNavigationItem}
                    onDragEnd={handleDragEnd}
                    sensors={sensors}
                  />
                ) : (
                  <Text color="gray.500" textAlign="center" py={8}>
                    Select a navigation menu to view its structure
                  </Text>
                )}
              </CardBody>
            </Card>
          </Box>
        </SimpleGrid>
      </VStack>

      {/* Delete Navigation Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteAlertClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Navigation Menu
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete "{navigationToDelete?.name}"? 
              This will also delete all navigation items associated with this menu. 
              This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={deleteNavigation} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Create Navigation Modal */}
      <CreateNavigationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={createNavigation}
        sites={sites}
      />

      {/* Edit Navigation Item Modal */}
      <EditNavigationItemModal
        isOpen={isEditItemModalOpen}
        onClose={() => setIsEditItemModalOpen(false)}
        item={editingItem}
        pages={pages}
        navigationId={selectedNavigation?.id}
        onSave={() => {
          fetchNavigationItems(selectedNavigation.id);
          setIsEditItemModalOpen(false);
        }}
      />
    </Container>
  );
};

// Navigation Tree Component using @dnd-kit
const NavigationTree = ({ items, onEdit, onDelete, onDragEnd, sensors }) => {
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
        <VStack spacing={2} align="stretch">
          {items.map((item) => (
            <SortableNavigationItem
              key={item.id}
              item={item}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
          {items.length === 0 && (
            <Text color="gray.500" textAlign="center" py={4}>
              No navigation items yet. Add your first item to get started.
            </Text>
          )}
        </VStack>
      </SortableContext>
    </DndContext>
  );
};

// Sortable Navigation Item Component
const SortableNavigationItem = ({ item, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      p={3}
      border="1px"
      borderColor={isDragging ? 'blue.300' : 'gray.200'}
      borderRadius="md"
      bg="white"
      _hover={{ borderColor: 'blue.300' }}
      opacity={isDragging ? 0.5 : 1}
    >
      <HStack justify="space-between">
        <HStack>
          <Box {...attributes} {...listeners} cursor="grab" _active={{ cursor: 'grabbing' }}>
            <DragHandleIcon color="gray.400" />
          </Box>
          <VStack align="start" spacing={1}>
            <HStack>
              <Text fontWeight="medium">{item.title}</Text>
              {item.target === '_blank' && <ExternalLinkIcon boxSize={3} />}
            </HStack>
            <Text fontSize="sm" color="gray.500">
              {item.url || item.page_path || 'No URL set'}
            </Text>
            <Badge colorScheme="purple" size="sm">
              {item.link_type}
            </Badge>
          </VStack>
        </HStack>
        <HStack>
          <IconButton
            size="sm"
            icon={<EditIcon />}
            variant="ghost"
            onClick={() => onEdit(item)}
          />
          <IconButton
            size="sm"
            icon={<DeleteIcon />}
            variant="ghost"
            colorScheme="red"
            onClick={() => onDelete(item.id)}
          />
        </HStack>
      </HStack>

      {/* Render children if any */}
      {item.children && item.children.length > 0 && (
        <Box ml={8} mt={2}>
          {item.children.map((child) => (
            <SortableNavigationItem
              key={child.id}
              item={child}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Create Navigation Modal
const CreateNavigationModal = ({ isOpen, onClose, onSubmit, sites }) => {
  const [formData, setFormData] = useState({
    name: '',
    handle: '',
    description: '',
    site_id: ''
  });

  const handleSubmit = () => {
    if (!formData.name || !formData.handle || !formData.site_id) {
      return;
    }
    onSubmit(formData);
    setFormData({ name: '', handle: '', description: '', site_id: '' });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Navigation Menu</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Main Navigation"
              />
            </FormControl>
            
            <FormControl isRequired>
              <FormLabel>Handle</FormLabel>
              <Input
                value={formData.handle}
                onChange={(e) => setFormData({ ...formData, handle: e.target.value })}
                placeholder="main-navigation"
              />
            </FormControl>
            
            <FormControl isRequired>
              <FormLabel>Site</FormLabel>
              <Select
                value={formData.site_id}
                onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
                placeholder="Select a site"
              >
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description of this navigation menu"
                rows={3}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit}>
            Create Navigation
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Edit Navigation Item Modal
const EditNavigationItemModal = ({ isOpen, onClose, item, pages, navigationId, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    link_type: 'page',
    page_id: '',
    url: '',
    target: '_self',
    css_class: ''
  });

  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || '',
        link_type: item.link_type || 'page',
        page_id: item.page_id || '',
        url: item.url || '',
        target: item.target || '_self',
        css_class: item.css_class || ''
      });
    } else {
      setFormData({
        title: '',
        link_type: 'page',
        page_id: '',
        url: '',
        target: '_self',
        css_class: ''
      });
    }
  }, [item]);

  const handleSubmit = async () => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const tenantId = user?.tenant_ids?.[0];

      const payload = {
        ...formData,
        navigation_id: navigationId
      };

      if (item) {
        // Update existing item
        await apiClient(`/navigations/${tenantId}/items/${item.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        // Create new item
        await apiClient(`/navigations/${tenantId}/items`, {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      onSave();
    } catch (error) {
      console.error('Error saving navigation item:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {item ? 'Edit Navigation Item' : 'Add Navigation Item'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Navigation item title"
              />
            </FormControl>
            
            <FormControl isRequired>
              <FormLabel>Link Type</FormLabel>
              <Select
                value={formData.link_type}
                onChange={(e) => setFormData({ ...formData, link_type: e.target.value })}
              >
                <option value="page">Page</option>
                <option value="url">External URL</option>
              </Select>
            </FormControl>
            
            {formData.link_type === 'page' ? (
              <FormControl isRequired>
                <FormLabel>Page</FormLabel>
                <Select
                  value={formData.page_id}
                  onChange={(e) => setFormData({ ...formData, page_id: e.target.value })}
                  placeholder="Select a page"
                >
                  {pages.map((page) => (
                    <option key={page.id} value={page.id}>
                      {page.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl isRequired>
                <FormLabel>URL</FormLabel>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://example.com"
                />
              </FormControl>
            )}
            
            <FormControl>
              <FormLabel>Target</FormLabel>
              <Select
                value={formData.target}
                onChange={(e) => setFormData({ ...formData, target: e.target.value })}
              >
                <option value="_self">Same window</option>
                <option value="_blank">New window</option>
              </Select>
            </FormControl>
            
            <FormControl>
              <FormLabel>CSS Class</FormLabel>
              <Input
                value={formData.css_class}
                onChange={(e) => setFormData({ ...formData, css_class: e.target.value })}
                placeholder="custom-class"
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit}>
            {item ? 'Update' : 'Add'} Item
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SitesNavigation;